/**
 * Custom hooks pour les endpoints Pricing non-standard (history, simulate,
 * supersede). Les actions REST conventionnelles (list, create) passent par
 * Refine `useList` / `useCreate`.
 *
 * Ces hooks s'appuient sur `axiosInstance` pour bénéficier de l'intercepteur
 * JWT refresh global.
 */

import { useCallback, useEffect, useState } from 'react';
import { axiosInstance } from '@/dataProvider/axiosInstance';
import type {
  PricingHistoryEntry,
  PricingProvider,
  PricingTier,
  SimulateRevenuePayload,
  SimulateRevenueResult,
  UnitType,
} from '../types';

interface HookState<T> {
  data: T | null;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Récupère l'historique complet pour un tuple (provider, tier, unitType).
 * Se déclenche automatiquement quand les 3 params sont définis.
 */
export function usePricingHistory(
  provider: PricingProvider | null,
  tier: PricingTier | null,
  unitType: UnitType | null,
): HookState<PricingHistoryEntry[]> & { refetch: () => void } {
  const [state, setState] = useState<HookState<PricingHistoryEntry[]>>({
    data: null,
    isLoading: false,
    error: null,
  });

  const fetchHistory = useCallback(async () => {
    if (!provider || !tier || !unitType) {
      setState({ data: null, isLoading: false, error: null });
      return;
    }
    setState((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      const res = await axiosInstance.get<{
        success: boolean;
        data: PricingHistoryEntry[];
      }>('/api/v1/admin/pricing/history', {
        params: { provider, tier, unitType },
      });
      setState({
        data: res.data?.data ?? [],
        isLoading: false,
        error: null,
      });
    } catch (err) {
      setState({
        data: null,
        isLoading: false,
        error: err instanceof Error ? err : new Error('Erreur inconnue'),
      });
    }
  }, [provider, tier, unitType]);

  useEffect(() => {
    void fetchHistory();
  }, [fetchHistory]);

  return { ...state, refetch: () => void fetchHistory() };
}

/**
 * Mutation supersede : DELETE /api/v1/admin/pricing/{pricingId}.
 * Clôture manuelle (scénario dépréciation) — aucun remplaçant n'est créé.
 * Pour remplacer un prix, utiliser `useCreate('admin/pricing')` qui clôture
 * automatiquement la ligne précédente via le handler `PublishPricing`.
 */
export function useSupersedePricing() {
  const [isPending, setPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutateAsync = useCallback(async (pricingId: string): Promise<void> => {
    setPending(true);
    setError(null);
    try {
      await axiosInstance.delete(`/api/v1/admin/pricing/${pricingId}`);
    } catch (err) {
      const e = err instanceof Error ? err : new Error('Erreur inconnue');
      setError(e);
      throw e;
    } finally {
      setPending(false);
    }
  }, []);

  return { mutateAsync, isPending, error };
}

/**
 * Mutation simulation — POST /api/v1/admin/pricing/simulate.
 * Renvoie CA brut / coût upstream / marge nette selon les volumes simulés.
 */
export function useSimulateRevenue() {
  const [data, setData] = useState<SimulateRevenueResult | null>(null);
  const [isPending, setPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutateAsync = useCallback(
    async (payload: SimulateRevenuePayload): Promise<SimulateRevenueResult> => {
      setPending(true);
      setError(null);
      try {
        const res = await axiosInstance.post<{
          success: boolean;
          data: SimulateRevenueResult;
        }>('/api/v1/admin/pricing/simulate', payload);
        const result = res.data?.data;
        setData(result);
        return result;
      } catch (err) {
        const e = err instanceof Error ? err : new Error('Erreur inconnue');
        setError(e);
        throw e;
      } finally {
        setPending(false);
      }
    },
    [],
  );

  const reset = useCallback(() => {
    setData(null);
    setError(null);
  }, []);

  return { data, mutateAsync, isPending, error, reset };
}
